// Web implementation
const isWeb = typeof window !== "undefined" && window.localStorage;
const isLambda =
  typeof process !== "undefined" && process.env.AWS_LAMBDA_FUNCTION_NAME;

// In-memory storage for Lambda and session storage fallback
const memoryStorage = new Map<string, string>();
const sessionMemoryStorage = new Map<string, string>();

export interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// Web implementation
const webStorage: Storage = {
  getItem: (key: string) => {
    if (isWeb) {
      return localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string) => {
    if (isWeb) {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    if (isWeb) {
      localStorage.removeItem(key);
    }
  },
};

// Web session storage implementation
const webSessionStorage: Storage = {
  getItem: (key: string) => {
    if (isWeb && window.sessionStorage) {
      return window.sessionStorage.getItem(key);
    }
    return sessionMemoryStorage.get(key) || null;
  },
  setItem: (key: string, value: string) => {
    if (isWeb && window.sessionStorage) {
      window.sessionStorage.setItem(key, value);
    } else {
      sessionMemoryStorage.set(key, value);
    }
  },
  removeItem: (key: string) => {
    if (isWeb && window.sessionStorage) {
      window.sessionStorage.removeItem(key);
    } else {
      sessionMemoryStorage.delete(key);
    }
  },
};

// Lambda implementation
const lambdaStorage: Storage = {
  getItem: (key: string) => {
    return memoryStorage.get(key) || null;
  },
  setItem: (key: string, value: string) => {
    memoryStorage.set(key, value);
  },
  removeItem: (key: string) => {
    memoryStorage.delete(key);
  },
};

// React Native implementation (to be implemented when needed)
let nativeStorage: Storage | null = null;
let nativeSessionStorage: Storage | null = null;

export const initializeNativeStorage = async (storage: Storage) => {
  nativeStorage = storage;
};

export const initializeNativeSessionStorage = async (storage: Storage) => {
  nativeSessionStorage = storage;
};

export const storage: Storage = {
  getItem: (key: string) => {
    if (nativeStorage) {
      return nativeStorage.getItem(key);
    }
    if (isLambda) {
      return lambdaStorage.getItem(key);
    }
    return webStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (nativeStorage) {
      nativeStorage.setItem(key, value);
    } else if (isLambda) {
      lambdaStorage.setItem(key, value);
    } else {
      webStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    if (nativeStorage) {
      nativeStorage.removeItem(key);
    } else if (isLambda) {
      lambdaStorage.removeItem(key);
    } else {
      webStorage.removeItem(key);
    }
  },
};

export const sessionStorage: Storage = {
  getItem: (key: string) => {
    if (nativeSessionStorage) {
      return nativeSessionStorage.getItem(key);
    }
    if (isLambda) {
      return lambdaStorage.getItem(key);
    }
    return webSessionStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (nativeSessionStorage) {
      nativeSessionStorage.setItem(key, value);
    } else if (isLambda) {
      lambdaStorage.setItem(key, value);
    } else {
      webSessionStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    if (nativeSessionStorage) {
      nativeSessionStorage.removeItem(key);
    } else if (isLambda) {
      lambdaStorage.removeItem(key);
    } else {
      webSessionStorage.removeItem(key);
    }
  },
};
